import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Download, FileText, Video, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

interface Resource {
  id: string;
  title: string;
  description: string | null;
  file_url: string | null;
  file_type: string | null;
  category: string | null;
  created_at: string;
}

const DashboardResources = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState<Resource[]>([]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }
    loadResources();
  };

  const loadResources = async () => {
    const { data, error } = await supabase
      .from("resources")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Error loading resources");
    } else {
      setResources(data || []);
    }
    setLoading(false);
  };

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return <FileText className="w-6 h-6" />;
    if (fileType.includes("video")) return <Video className="w-6 h-6" />;
    if (fileType.includes("image")) return <ImageIcon className="w-6 h-6" />;
    return <FileText className="w-6 h-6" />;
  };

  const handleDownload = async (resource: Resource) => {
    if (!resource.file_url) return;
    
    try {
      // Check if it's a Supabase storage URL
      if (resource.file_url.includes('supabase.co/storage')) {
        // Direct Supabase storage download
        window.open(resource.file_url, '_blank');
        toast.success("Download started");
      } else {
        // External URL - open in new tab
        window.open(resource.file_url, '_blank');
        toast.success("Opening resource");
      }
    } catch (error) {
      toast.error("Error accessing resource");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="space-y-8">
          <div className="text-center space-y-4 animate-fade-in">
            <h1 className="text-4xl font-bold">Resource Library</h1>
            <p className="text-xl text-muted-foreground">
              Access templates, guides, and course materials
            </p>
          </div>

          {resources.length === 0 ? (
            <Card className="border-2 animate-fade-in">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No Resources Yet</h3>
                <p className="text-muted-foreground">
                  Resources will be added here as you progress through the course
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map((resource, index) => (
                <Card
                  key={resource.id}
                  className="border-2 hover:border-primary transition-all hover:shadow-xl hover:shadow-primary/20 animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4 text-white">
                      {getFileIcon(resource.file_type)}
                    </div>
                    <CardTitle className="line-clamp-2">{resource.title}</CardTitle>
                    {resource.description && (
                      <CardDescription className="line-clamp-3">
                        {resource.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    {resource.category && (
                      <div className="text-xs text-primary font-medium mb-3 uppercase tracking-wide">
                        {resource.category}
                      </div>
                    )}
                    {resource.file_url && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleDownload(resource)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download / View
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardResources;
